import React from "react";

import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import {
  alpha,
  Box,
  Button,
  Link,
  SvgIcon,
  Typography,
  useTheme,
} from "@mui/material";
import { Link as RouterLink } from "react-router";

import { getEmptyStateSx } from "src/shell/styles/themeUtils";

interface ProjectsEmptyStateProps {
  orgAlias?: string;
  filteredMode?: boolean;
  onClearFilters?: () => void;
}

const HubNodeDiagram = () => {
  const theme = useTheme();
  return (
    <SvgIcon
      sx={{ width: 180, height: 160, color: "primary.main" }}
      viewBox="0 0 180 160"
    >
      <circle
        cx="90"
        cy="40"
        r="28"
        fill={alpha(theme.palette.primary.main, 0.12)}
      />
      <circle cx="90" cy="40" r="12" fill="currentColor" />
      <circle
        cx="40"
        cy="80"
        r="10"
        fill={theme.palette.background.paper}
        stroke="currentColor"
        strokeWidth="2"
      />
      <circle
        cx="140"
        cy="80"
        r="10"
        fill={theme.palette.background.paper}
        stroke="currentColor"
        strokeWidth="2"
      />
      <circle
        cx="60"
        cy="120"
        r="10"
        fill={theme.palette.background.paper}
        stroke="currentColor"
        strokeWidth="2"
      />
      <circle
        cx="120"
        cy="120"
        r="10"
        fill={theme.palette.background.paper}
        stroke="currentColor"
        strokeWidth="2"
      />
      <line
        x1="90"
        y1="52"
        x2="40"
        y2="74"
        stroke="currentColor"
        strokeOpacity="0.4"
        strokeWidth="1"
        strokeDasharray="4 4"
      />
      <line
        x1="90"
        y1="52"
        x2="140"
        y2="74"
        stroke="currentColor"
        strokeOpacity="0.4"
        strokeWidth="1"
        strokeDasharray="4 4"
      />
      <line
        x1="90"
        y1="52"
        x2="60"
        y2="114"
        stroke="currentColor"
        strokeOpacity="0.4"
        strokeWidth="1"
        strokeDasharray="4 4"
      />
      <line
        x1="90"
        y1="52"
        x2="120"
        y2="114"
        stroke="currentColor"
        strokeOpacity="0.4"
        strokeWidth="1"
        strokeDasharray="4 4"
      />
    </SvgIcon>
  );
};

export const ProjectsEmptyState: React.FC<ProjectsEmptyStateProps> = ({
  orgAlias,
  filteredMode = false,
  onClearFilters,
}) => {
  const theme = useTheme();
  return (
    <Box
      sx={() => ({
        ...getEmptyStateSx(theme),
        py: 5,
        borderStyle: "solid",
      })}
    >
      {filteredMode ? (
        <>
          <Typography variant="subtitle1" fontWeight={600}>
            No projects match your filters
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            textAlign="center"
            maxWidth={520}
          >
            Try adjusting your search or filters to find what you&apos;re
            looking for.
          </Typography>
          {onClearFilters && (
            <Button variant="outlined" onClick={onClearFilters}>
              Clear filters
            </Button>
          )}
        </>
      ) : (
        <>
          <HubNodeDiagram />
          <Typography variant="subtitle1" fontWeight={600}>
            No projects yet
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            textAlign="center"
            maxWidth={520}
          >
            Projects group systems, resources, and deployments under one
            namespace. Create your first project to start deploying.
          </Typography>
          {orgAlias && (
            <Button
              variant="contained"
              component={RouterLink}
              to={`/organizations/${orgAlias}/projects/create`}
              endIcon={<ArrowForwardIcon />}
            >
              Create project
            </Button>
          )}
          <Link
            component={RouterLink}
            to="#"
            variant="body2"
            sx={{ fontWeight: 500 }}
          >
            Learn about projects →
          </Link>
        </>
      )}
    </Box>
  );
};

export default ProjectsEmptyState;
